/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import api from '@/api';

export const namespaced = true;

export const state = {
  services: [],
  userStaffLeadServices: [],
  userStaffLeadServicesLoading: false,
  loading: false,
  isServiceEdit: false,
  isInspectionServiceEdit: false,
  currentService: {
    observations: [],
  },
  projectId: null,
  serverError: '',
  submitting: false,
  distinctStaffLeadNames: [],
  distinctStaffLeadNamesLoading: false,
  distinctReviewerNames: [],
  distinctReviewerNamesLoading: false,
  distinctReviewAssignerNames: [],
  distinctReviewAssignerNamesLoading: false,
};

export const actions = {
  async fetchOpenServicesForCurrentUser({ commit }) {
    commit('SET_USER_STAFF_LEAD_LOADING', true);
    await api.get('/staffleadservices')
      .then((response) => {
        commit('SET_USER_STAFF_LEAD_SERVICES', response.data);
        commit('SET_USER_STAFF_LEAD_LOADING', false);
      })
      .catch(() => {
        commit('SET_USER_STAFF_LEAD_LOADING', false);
      });
  },
  async clearCurrentService({ commit }) {
    commit('SET_CURRENT_SERVICE', null);
  },
  async resetServices({ commit }) {
    commit('SET_PROJECT_ID', 0);
    commit('SET_SERVICES', []);
  },
  async fetchServices({ commit, state }, projectId) {
    if (state.projectId !== projectId) {
      commit('SET_LOADING', true);
      await api.get(`/project/${projectId}/services`)
        .then((response) => {
          commit('SET_SERVICES', response.data);
          commit('SET_LOADING', false);
          commit('SET_PROJECT_ID', projectId);
        })
        .catch(() => {
          commit('SET_LOADING', false);
        });
    }
  },
  async forceFetchServices({ commit }, projectId) {
    commit('SET_LOADING', true);
    await api.get(`/project/${projectId}/services`)
      .then((response) => {
        commit('SET_SERVICES', response.data);
        commit('SET_LOADING', false);
        commit('SET_PROJECT_ID', projectId);
      })
      .catch(() => {
        commit('SET_LOADING', false);
      });
  },
  async deleteService({ commit, dispatch, rootState }, { serviceId, submittedCallback }) {
    commit('START_SUBMITTING');
    await api.delete(`/service/delete/${serviceId}`)
      .then(() => {
        commit('SET_SERVER_ERROR', '');
        commit('STOP_SUBMITTING');
        const { id } = rootState.CurrentProject.currentProject;
        dispatch('CurrentProject/fetchProjectDeleteMessage', { projectId: id }, { root: true });
        dispatch('CurrentProject/refreshLatestEvents', { projectId: id }, { root: true });
        submittedCallback();
      })
      .catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING');
      });
  },
  async forceFetchServicesWithNoLoadingIndicator({ commit }, projectId) {
    await api.get(`/project/${projectId}/services`)
      .then((response) => {
        commit('SET_SERVICES', response.data);
        commit('REFRESH_CURRENT_SERVICE', response.data);
        commit('SET_PROJECT_ID', projectId);
      })
      .catch(() => {
      });
  },
  async fetchService({ commit, getters, dispatch }, { projectId, id }) {
    const service = getters.getService(id);
    if (service) {
      commit('SET_CURRENT_SERVICE', service);
    } else {
      // if service wasn't already loaded, load all services and try again
      // if not found this time, it's ok to set a nil service in state
      await dispatch('fetchServices', projectId);
      const serviceSecondAttempt = getters.getService(id);
      commit('SET_CURRENT_SERVICE', serviceSecondAttempt);
    }
  },
  async createEntity({ commit, dispatch, rootState }, { entity, submittedCallback }) {
    api.post('/service/create', entity)
      .then((response) => {
        commit('ADD_NEW_SERVICE', response.data);
        commit('SET_CURRENT_SERVICE', response.data);
        commit('SET_SERVER_ERROR', '');
        commit('SET_PROJECT_ID', response.data.primaryProjectId);
        commit('STOP_SUBMITTING');
        if (rootState.CurrentProject.currentProject) {
          const currentProjectId = rootState.CurrentProject.currentProject.id;
          dispatch('CurrentProject/clearCurrentProject', null, { root: true });
          dispatch('CurrentProject/fetch',
            { projectId: currentProjectId },
            { root: true });
        }
        if (submittedCallback) submittedCallback(response.data.id);
      })
      .catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING');
      });
  },
  async updateEntity({ commit, dispatch, rootState }, { entity, submittedCallback }) {
    const submittedEntity = entity;
    submittedEntity.serviceInstruments = [];
    const endpoint = entity.isServiceEdit ? '/service/update' : '/service/associatePropertiesToService';
    await api.put(endpoint, entity)
      .then((response) => {
        commit('SET_CURRENT_SERVICE', response.data);
        commit('SET_SERVICE_EDIT', false);
        commit('SET_SERVER_ERROR', '');
        commit('STOP_SUBMITTING');
        if (rootState.CurrentProject.currentProject) {
          const { id } = rootState.CurrentProject.currentProject;
          dispatch('CurrentProject/clearCurrentProject', null, { root: true });
          dispatch('CurrentProject/fetch',
            { projectId: id },
            { root: true });
        }
        if (submittedCallback) submittedCallback();
      }).catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING');
      });
  },
  async updateDocumentFolder({ commit }, { id, folderId }) {
    commit('START_SUBMITTING');
    await api.patch(`/service/${id}/updateDocumentFolder`, { folderId })
      .then((response) => {
        commit('SET_DOCUMENT_FOLDER', response.data);
        commit('SET_SERVER_ERROR', '');
        commit('STOP_SUBMITTING');
      })
      .catch((error) => {
        commit('SET_SERVER_ERROR', error.response.data);
        commit('STOP_SUBMITTING');
      });
  },
  setCurrentObservations({ commit }, { observations }) {
    commit('SET_CURRENT_OBSERVATIONS', observations);
  },
  setObservation({ commit }, { observation }) {
    commit('SET_OBSERVATION', { observation });
  },
  switchToEdit({ commit }) {
    commit('SET_SERVICE_EDIT', true);
  },
  switchToInspectionEdit({ commit }) {
    commit('SET_INSPECTION_SERVICE_EDIT', true);
  },
  stopEditing({ commit }) {
    commit('SET_SERVICE_EDIT', false);
  },
  stopInspectionEditing({ commit }) {
    commit('SET_INSPECTION_SERVICE_EDIT', false);
  },
  startSubmitting({ commit }) {
    commit('START_SUBMITTING');
  },
  stopSubmitting({ commit }) {
    commit('STOP_SUBMITTING');
  },
  async fetchStaffLeadNames({ commit, state }) {
    // only fetch if not already loaded
    if (!state.distinctStaffLeadNames || state.distinctStaffLeadNames.length === 0) {
      commit('SET_STAFF_LEAD_LOADING', true);
      await api.get('/service/staffLeadNames')
        .then((response) => {
          // note, a list of strings is returned from the API, but we need it as
          // and object w/ name and id attributes
          // respect order in which users are returned from the backend
          commit('SET_STAFF_LEAD', response.data.map(sl => ({ name: sl, id: sl })));
          commit('SET_STAFF_LEAD_LOADING', false);
        })
        .catch(() => {
          commit('SET_STAFF_LEAD_LOADING', false);
        });
    }
  },
  async fetchReviewerNames({ commit, state }) {
    // only fetch if not already loaded
    if (!state.distinctReviewerNames || state.distinctReviewerNames.length === 0) {
      commit('SET_REVIEWER_LOADING', true);
      await api.get('/service/reviewers')
        .then((response) => {
          // note, a list of strings is returned from the API, but we need it as
          // and object w/ name and id attributes
          commit('SET_REVIEWER', response.data.sort().map(rn => ({ name: rn, id: rn })));
          commit('SET_REVIEWER_LOADING', false);
        })
        .catch(() => {
          commit('SET_REVIEWER_LOADING', false);
        });
    }
  },
  async fetchReviewAssignerNames({ commit, state }) {
    // only fetch if not already loaded
    if (!state.distinctReviewAssignerNames || state.distinctReviewAssignerNames.length === 0) {
      commit('SET_REVIEW_ASSIGNER_LOADING', true);
      await api.get('/service/reviewassigners')
        .then((response) => {
          // note, a list of strings is returned from the API, but we need it as
          // and object w/ name and id attributes
          commit('SET_REVIEW_ASSIGNER', response.data.sort().map(ran => ({ name: ran, id: ran })));
          commit('SET_REVIEW_ASSIGNER_LOADING', false);
        })
        .catch(() => {
          commit('SET_REVIEW_ASSIGNER_LOADING', false);
        });
    }
  },
};

export const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_USER_STAFF_LEAD_SERVICES(state, services) {
    state.userStaffLeadServices = services;
  },
  SET_USER_STAFF_LEAD_LOADING(state, loading) {
    state.userStaffLeadServicesLoading = loading;
  },
  SET_SERVICES(state, services) {
    state.services = services;
  },
  SET_PROJECT_ID(state, projectId) {
    state.projectId = projectId;
  },
  SET_SERVICE_EDIT(state, isServiceEdit) {
    state.isServiceEdit = isServiceEdit;
  },
  SET_INSPECTION_SERVICE_EDIT(state, isInspectionServiceEdit) {
    state.isInspectionServiceEdit = isInspectionServiceEdit;
  },
  START_SUBMITTING(state) {
    state.submitting = true;
  },
  STOP_SUBMITTING(state) {
    state.submitting = false;
  },
  SET_SERVER_ERROR(state, error) {
    state.serverError = error;
  },
  ADD_NEW_SERVICE(state, service) {
    state.services.push(service);
  },
  SET_CURRENT_SERVICE(state, service) {
    state.currentService = service;
    if (service) {
      // replace the service object in the array of services
      const currentServiceIndex = state.services.findIndex(svc => svc.id === service.id);
      if (currentServiceIndex >= 0) {
        state.services.splice(currentServiceIndex, 1, service);
      } else {
        state.services.splice(0, 0, service);
      }
      state.isInspectionServiceEdit = false;
      state.isServiceEdit = false;
    } else {
      state.currentService = { observations: [] };
    }
  },
  REFRESH_CURRENT_SERVICE(state, services) {
    if (services) {
      const currentServiceIndex = services.findIndex(x => x.id === state.currentService.id);
      if (currentServiceIndex >= 0) {
        state.currentService = services[currentServiceIndex];
      }
    }
  },
  SET_DOCUMENT_FOLDER(state, folderId) {
    state.currentService.documentFolderId = folderId;
  },
  SET_CURRENT_OBSERVATIONS(state, observations) {
    state.currentService.observations = observations;
  },
  SET_OBSERVATION(state, { observation }) {
    const obs = state.currentService.observations.filter(ob => ob.typeId === observation.typeId)[0];
    obs.ackTypeId = observation.ackTypeId;
    obs.notes = observation.notes;
  },
  SET_STAFF_LEAD(state, staffLeadList) {
    state.distinctStaffLeadNames = staffLeadList;
  },
  SET_STAFF_LEAD_LOADING(state, staffLeadLoading) {
    state.distinctStaffLeadNamesLoading = staffLeadLoading;
  },
  SET_REVIEWER(state, reviewerList) {
    state.distinctReviewerNames = reviewerList;
  },
  SET_REVIEWER_LOADING(state, reviewerLoading) {
    state.distinctReviewerNamesLoading = reviewerLoading;
  },
  SET_REVIEW_ASSIGNER(state, reviewAssignerList) {
    state.distinctReviewAssignerNames = reviewAssignerList;
  },
  SET_REVIEW_ASSIGNER_LOADING(state, reviewAssignerLoading) {
    state.distinctReviewAssignerNamesLoading = reviewAssignerLoading;
  },
};

export const getters = {
  orderedServices: (state) => {
    const clonedServices = [...state.services];

    clonedServices
      .sort((a, b) => {
        if (a.statusId === 1 && b.statusId === 1) {
          return a.statusId - b.statusId || new Date(b.openDate) - new Date(a.openDate) || b.id - a.id;
        }
        return a.statusId - b.statusId || new Date(b.openDate) - new Date(a.openDate) || b.id - a.id;
      });
    return clonedServices;
  },
  getDefaultService: (state) => {
    if (state.services.length > 0) {
      return getters.orderedServices(state)[0];
    }
    return null;
  },
  getService: state => (id) => {
    if (id) return state.services.find(a => a.id === Number(id));
    return null;
  },
  isInspectionServiceImagery: () => (service) => {
    if (service && service.typeIds && service.categoryId) return service.typeIds.includes(48);
    return false;
  },
  currentServiceObservations: (state) => {
    if (state.currentService && state.currentService.observations) {
      return state.currentService.observations;
    }
    return null;
  },
};
